import _ from 'underscore';
import React, { useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  LocationTarget as LocationTargetType,
  getIsRadiusTarget as defaultGetIsRadiusTarget,
  getIsBoundaryTarget as defaultGetIsBoundaryTarget,
} from '@bingads-webui-react/bing-maps';

import { targetOptions } from './utils/target-options';
import { getLocationTargetingStyles } from './styles/location-targeting-style';
import { LocationTargetingSelection } from './location-targeting-selection';
import { LocationTargetingIntent } from './location-targeting-intent';
import { LocationTargetErrorCallout, LocationTargetErrorMessage } from './location-targeting-error';

export function LocationTargeting(props) {
  const {
    // common props
    strings,
    onNotifyValidationResult,
    isLoading,
    // Location choiceGroups props
    optionsConfig,
    selectedLocationOption,
    onChangeLocationOption,
    // LocationTargetsEditor props, complete props refers to "@bingads-webui-react/bing-maps"
    allowNegativeLocationTargeting,
    locationTargets,
    negativeLocationTargets,
    onLocationTargetsChange,
    onNegativeLocationTargetsChange,
    defaultRadiusSize,
    defaultRadiusUnit,
    mapRef,
    maxLocationTargets,
    maxRadiusInMiles,
    maxRadiusInKilometers,
    onLocationTargetsGetErrorMessage,
    onNegativeLocationTargetsGetErrorMessage,
    onSingleLocationTargetGetErrorMessage,
    isReadonly,
    getIsRadiusTarget,
    getIsBoundaryTarget,
    searchResultsFooter,
    searchResultsFooterOnClick,
    defaultSelectedSearchType,
    isMixedLocationSearch,
    showSearchTypeOptions,
    useInternalValidation,
    validationProps: {
      supportedCountries,
      errorsMap,
      getErrorMessages,
      getLocationTargetsForCallout,
    },
    // Targeting Intent
    shouldShowTargetingIntent,
    isPeopleInLocationChecked,
    isPeopleSearchingForLocationChecked,
    onPeopleInLocationChange,
    onPeopleSearchingForLocationChange,
  } = props;

  const classes = useDefaultStyles(getLocationTargetingStyles);
  const [errorCalloutProps, setErrorCalloutProps] = useState({});

  const handleErrorCalloutHide = useCallback(() => { setErrorCalloutProps({}); }, []);
  const handleErrorCalloutShow = useCallback((event, locationTarget, error) => {
    setErrorCalloutProps({
      target: event.target,
      error,
      locationTargets: getLocationTargetsForCallout(locationTarget, error),
    });
  }, [getLocationTargetsForCallout]);

  // Validate whole location targets
  const handleLocationTargetsValidate = useCallback(() => {
    const [validationResultMessage, editorMessage] = getErrorMessages({
      errorsMap,
      supportedCountries,
    });

    onNotifyValidationResult(validationResultMessage);

    return editorMessage;
  }, [errorsMap, getErrorMessages, onNotifyValidationResult, supportedCountries]);

  // Error messages to display next to a specific location target
  const handleSingleLocationGetTargetErrorMessage = useCallback(locationTarget => (
    errorsMap && errorsMap.has(locationTarget) && (
      <LocationTargetErrorMessage
        error={errorsMap.get(locationTarget)}
        locationTarget={locationTarget}
        onErrorCalloutShow={handleErrorCalloutShow}
        supportedCountries={supportedCountries}
      />
    )
  ), [errorsMap, handleErrorCalloutShow, supportedCountries]);

  // validate negative location targets
  const handleNegativeLocationTargetsValidate = useCallback(() => {
    const [validationResultMessage, editorMessage] = getErrorMessages({
      errorsMap,
      supportedCountries,
      isNegativeLocationTarget: true,
    });

    onNotifyValidationResult(validationResultMessage);

    return editorMessage;
  }, [errorsMap, getErrorMessages, onNotifyValidationResult, supportedCountries]);

  const handleLocationTargetRemove = useCallback((toRemove) => {
    onLocationTargetsChange(_.without(locationTargets, toRemove));
  }, [onLocationTargetsChange, locationTargets]);

  const validationProps = useMemo(() => {
    if (!useInternalValidation) {
      return {
        onLocationTargetsGetErrorMessage,
        onSingleLocationTargetGetErrorMessage,
        onNegativeLocationTargetsGetErrorMessage,
      };
    }

    return {
      onLocationTargetsGetErrorMessage: handleLocationTargetsValidate,
      onSingleLocationTargetGetErrorMessage: handleSingleLocationGetTargetErrorMessage,
      onNegativeLocationTargetsGetErrorMessage: handleNegativeLocationTargetsValidate,
    };
  }, [
    handleLocationTargetsValidate, handleSingleLocationGetTargetErrorMessage, onLocationTargetsGetErrorMessage,
    onNegativeLocationTargetsGetErrorMessage, onSingleLocationTargetGetErrorMessage, useInternalValidation, handleNegativeLocationTargetsValidate,
  ]);

  return (
    <div className={classNames('location-targeting', classes.LocationTargeting)}>
      <div className="location-targeting-body">
        <LocationTargetingSelection
          isLoading={isLoading}
          updateErrorMessage={onNotifyValidationResult}
          allowNegativeLocationTargeting={allowNegativeLocationTargeting}
          locationTargets={locationTargets}
          negativeLocationTargets={negativeLocationTargets}
          onLocationTargetsChange={onLocationTargetsChange}
          onNegativeLocationTargetsChange={onNegativeLocationTargetsChange}
          defaultRadiusSize={defaultRadiusSize}
          defaultRadiusUnit={defaultRadiusUnit}
          mapRef={mapRef}
          maxLocationTargets={maxLocationTargets}
          maxRadiusInMiles={maxRadiusInMiles}
          maxRadiusInKilometers={maxRadiusInKilometers}
          isReadonly={isReadonly}
          getIsRadiusTarget={getIsRadiusTarget}
          getIsBoundaryTarget={getIsBoundaryTarget}
          searchResultsFooter={searchResultsFooter}
          searchResultsFooterOnClick={searchResultsFooterOnClick}
          defaultSelectedSearchType={defaultSelectedSearchType}
          isMixedLocationSearch={isMixedLocationSearch}
          strings={strings}
          showSearchTypeOptions={showSearchTypeOptions}
          // choicegroup
          optionsConfig={optionsConfig}
          selectedLocationOption={selectedLocationOption}
          onChangeLocationOption={onChangeLocationOption}
          {...validationProps}
        />
        {
          (shouldShowTargetingIntent && selectedLocationOption !== targetOptions.everywhere) && (
            <LocationTargetingIntent
              isLoading={isLoading}
              updateErrorMessage={onNotifyValidationResult}
              isPeopleInLocationChecked={isPeopleInLocationChecked}
              isPeopleSearchingForLocationChecked={isPeopleSearchingForLocationChecked}
              onPeopleInLocationChange={onPeopleInLocationChange}
              onPeopleSearchingForLocationChange={onPeopleSearchingForLocationChange}
            />
          )
        }
        <LocationTargetErrorCallout
          {...errorCalloutProps}
          onDismiss={handleErrorCalloutHide}
          onLocationTargetRemove={handleLocationTargetRemove}
        />
      </div>
    </div>
  );
}

LocationTargeting.propTypes = {
  strings: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  onNotifyValidationResult: PropTypes.func,
  optionsConfig: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ])).isRequired,
  selectedLocationOption: PropTypes.string.isRequired,
  onChangeLocationOption: PropTypes.func.isRequired,

  allowNegativeLocationTargeting: PropTypes.bool,
  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTargetType)).isRequired,
  negativeLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTargetType)),
  onLocationTargetsChange: PropTypes.func.isRequired,
  onNegativeLocationTargetsChange: PropTypes.func,
  defaultRadiusSize: PropTypes.number,
  defaultRadiusUnit: PropTypes.oneOf(['mi', 'km']),
  /**
   * Ref that can be used to trigger actions in the map web control like zooming.
   */
  mapRef: PropTypes.shape({
    current: PropTypes.shape({
      hideTooltip: PropTypes.func,
      showTooltip: PropTypes.func,
    }),
  }),
  maxLocationTargets: PropTypes.number,
  maxRadiusInMiles: PropTypes.number,
  maxRadiusInKilometers: PropTypes.number,
  onLocationTargetsGetErrorMessage: PropTypes.func,
  onNegativeLocationTargetsGetErrorMessage: PropTypes.func,
  onSingleLocationTargetGetErrorMessage: PropTypes.func,
  isReadonly: PropTypes.bool,
  /**
   * Function that receives a location and determines if it is or isn't available for radius
   * targeting.
   */
  getIsRadiusTarget: PropTypes.func,
  /**
   * Function that receives a location and determines if it is or isn't available for boundary
   * targeting.
   */
  getIsBoundaryTarget: PropTypes.func,
  searchResultsFooter: PropTypes.func,
  searchResultsFooterOnClick: PropTypes.func,
  defaultSelectedSearchType: PropTypes.string,
  isMixedLocationSearch: PropTypes.bool,
  showSearchTypeOptions: PropTypes.bool,
  /**
   * Whether to use internal validation or not. If true,  validationProps is required.
   */
  useInternalValidation: PropTypes.bool,
  /**
   * Validation config for selected locations.
   */
  validationProps: PropTypes.shape({
    supportedCountries: PropTypes.string,
    errorsMap: PropTypes.instanceOf(Map),
    getErrorMessages: PropTypes.func.isRequired,
    getLocationTargetsForCallout: PropTypes.func.isRequired,
  }),

  shouldShowTargetingIntent: PropTypes.bool,
  isPeopleInLocationChecked: PropTypes.bool,
  isPeopleSearchingForLocationChecked: PropTypes.bool,
  onPeopleInLocationChange: PropTypes.func.isRequired,
  onPeopleSearchingForLocationChange: PropTypes.func.isRequired,
};

LocationTargeting.defaultProps = {
  strings: {},
  isLoading: false,
  onNotifyValidationResult: _.noop,

  allowNegativeLocationTargeting: true,
  negativeLocationTargets: [],
  onNegativeLocationTargetsChange: _.noop,
  defaultRadiusSize: 25,
  defaultRadiusUnit: 'mi',
  mapRef: null,
  maxLocationTargets: 25,
  maxRadiusInMiles: 500,
  maxRadiusInKilometers: 800,
  onLocationTargetsGetErrorMessage: _.noop,
  onNegativeLocationTargetsGetErrorMessage: _.noop,
  onSingleLocationTargetGetErrorMessage: _.noop,
  isReadonly: false,
  getIsRadiusTarget: defaultGetIsRadiusTarget,
  getIsBoundaryTarget: defaultGetIsBoundaryTarget,
  searchResultsFooter: null,
  searchResultsFooterOnClick: null,
  defaultSelectedSearchType: undefined,
  isMixedLocationSearch: false,
  showSearchTypeOptions: true,
  useInternalValidation: true,
  validationProps: {
    supportedCountries: 'United States',
    errorsMap: new Map(),
    getErrorMessages: _.noop,
    getLocationTargetsForCallout: _.noop,
  },

  shouldShowTargetingIntent: false,
  isPeopleInLocationChecked: true,
  isPeopleSearchingForLocationChecked: true,
};
